import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Anime } from "@/mocks/anime";
import { useFavorites } from "@/hooks/useFavorites";

interface HeroCarouselProps {
  items: Anime[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();

  useEffect(() => {
    if (paused || items.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5200);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  if (!items.length) return null;
  const current = items[index];
  const favorited = isFavorite(current.id);

  const go = (next: number) => {
    setIndex((next + items.length) % items.length);
  };

  return (
    <section
      className="relative w-full h-[300px] md:h-[400px] overflow-hidden rounded-lg bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="热门动漫焦点轮播"
    >
      {items.map((item, i) => (
        <img
          key={item.id}
          src={item.cover}
          alt={`${item.title} 主视觉图`}
          title={`${item.title} - 高清动漫在线观看`}
          className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-transparent to-primary-950/30" />

      <div className="relative z-10 h-full flex flex-col justify-end md:justify-center px-5 md:px-10 pb-10 md:pb-0 max-w-full">
        <div key={current.id} className="animate-fade-up max-w-[440px]">
          <div className="flex items-center gap-2 text-[11px] text-accent-300">
            <span className="px-2 py-0.5 rounded-full bg-accent-500 text-primary-950 font-semibold">本周焦点</span>
            <span className="text-background-200">{current.region} · {current.year}</span>
          </div>
          <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-background-50 leading-tight">
            {current.title}
          </h2>
          <p className="mt-2 text-[12px] md:text-[13px] leading-relaxed text-background-200 line-clamp-2 md:line-clamp-3">
            {current.summary}
          </p>
          <div className="mt-3 flex items-center flex-wrap gap-2 text-[11px] text-background-100">
            <span className="flex items-center gap-1 text-accent-400">
              <i className="ri-star-fill" /> {current.rating.toFixed(1)}
            </span>
            <span className="w-1 h-1 rounded-full bg-background-500" />
            <span>{current.status}</span>
            <span className="w-1 h-1 rounded-full bg-background-500" />
            <span>{current.categories.join(" / ")}</span>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Link
              to={`/detail/${current.id}`}
              className="h-9 px-4 rounded-md bg-accent-500 text-primary-950 text-[13px] font-semibold flex items-center gap-1.5 hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill" />
              查看详情
            </Link>
            <button
              type="button"
              onClick={() => toggleFavorite(current.id)}
              className={`h-9 px-3 rounded-md text-[13px] flex items-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap ${
                favorited
                  ? "bg-background-50 text-primary-900"
                  : "border border-background-400/60 text-background-100 hover:bg-background-50/10"
              }`}
            >
              <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} />
              {favorited ? "已收藏" : "收藏"}
            </button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 right-4 z-10 flex items-center gap-2">
        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一部"
          className="w-7 h-7 rounded-full bg-background-50/20 text-background-50 flex items-center justify-center hover:bg-background-50/35 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        <div className="flex items-center gap-1.5">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`切换到 ${item.title}`}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-5 bg-accent-500" : "w-1.5 bg-background-400/70"
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一部"
          className="w-7 h-7 rounded-full bg-background-50/20 text-background-50 flex items-center justify-center hover:bg-background-50/35 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>
    </section>
  );
}